import { Component, EventEmitter, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { generateTableColumns } from '@ui-kit/webkit/components/crud-item/crud-item-decorator';
import { AppTableDisplayColumn, TableActionItem } from '@ui-kit/webkit/components/crud-table/crud-table.model';
import { CrudAtokuStation } from '../models/crud-atoku-station.model';
import { AtokuStation } from '../models/atoku-station.model';
import { EditStationDialogComponent, EditStationDialogData } from './dialogs/edit-station-dialog/edit-station-dialog.component';
import { AtokuPricingProfileLookup, AtokuService } from '../services/atoku.service';

@Component({
    selector: 'app-atoku-stations',
    templateUrl: './atoku-stations.component.html',
    styleUrls: ['./atoku-stations.component.scss']
})
export class AtokuStationsComponent implements OnInit {
    readonly crudStationModel = CrudAtokuStation;

    displayColumns: AppTableDisplayColumn[] = generateTableColumns(CrudAtokuStation);
    pricingProfiles: AtokuPricingProfileLookup[] = [];
    selectedPricingProfileCode = '';
    stationCategories: string[] = [];
    tableData: CrudAtokuStation[] = [];

    private stations: AtokuStation[] = [];
    private selectedProfilePrices: Record<string, number> = {};

    private readonly refreshStationsAction = new EventEmitter<void>();
    private readonly createStationAction = new EventEmitter<void>();
    private readonly editStationAction = new EventEmitter<CrudAtokuStation>();
    private readonly removeStationAction = new EventEmitter<CrudAtokuStation>();

    readonly additionalActions: TableActionItem[] = [
        {
            icon: 'refresh',
            label: 'Refresh data',
            actionDataSource: 'None',
            enabled: 'Always',
            includeData: false,
            eventEmitter: this.refreshStationsAction,
            keepSelectedRowOnClick: false
        },
        {
            icon: 'add',
            label: 'Create station',
            actionDataSource: 'None',
            enabled: 'Always',
            includeData: false,
            eventEmitter: this.createStationAction,
            keepSelectedRowOnClick: false
        },
        {
            icon: 'edit',
            label: 'Edit station',
            actionDataSource: 'SelectedRow',
            enabled: 'RowSelected',
            includeData: true,
            eventEmitter: this.editStationAction,
            keepSelectedRowOnClick: false
        },
        {
            icon: 'delete',
            label: 'Remove station',
            actionDataSource: 'SelectedRow',
            enabled: 'RowSelected',
            includeData: true,
            eventEmitter: this.removeStationAction,
            keepSelectedRowOnClick: false
        }
    ];

    constructor(
        private readonly dialog: MatDialog,
        private readonly atokuService: AtokuService
    ) {
        this.refreshStationsAction.subscribe(() => this.refreshStations());
        this.createStationAction.subscribe(() => this.openAddDialog());
        this.editStationAction.subscribe((station) => this.openEditDialog(station));
        this.removeStationAction.subscribe((station) => this.removeStation(station));
        this.updatePriceColumnLabel();
    }

    ngOnInit(): void {
        this.loadCategories();
        this.loadProfiles();
        this.loadStations();
    }

    onPricingProfileChange(): void {
        this.updatePriceColumnLabel();

        if (!this.selectedPricingProfileCode) {
            this.selectedProfilePrices = {};
            this.rebuildTableData();
            return;
        }

        this.atokuService.getPricingProfiles().subscribe(profiles => {
            const selectedProfile = profiles.find(profile => profile.code === this.selectedPricingProfileCode);
            const prices = selectedProfile?.['subSpaces'] ?? selectedProfile?.['stations'];
            this.selectedProfilePrices = this.toPriceMap(prices);
            this.rebuildTableData();
        });
    }

    private openAddDialog(): void {
        const dialogRef = this.dialog.open(EditStationDialogComponent, {
            width: '500px',
            data: {
                station: {
                    id: this.getNextId(),
                    name: '',
                    category: this.stationCategories[0] ?? ''
                },
                categories: this.stationCategories
            } as EditStationDialogData
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!result) {
                return;
            }

            this.atokuService.createStation(result).subscribe(() => this.loadStations());
        });
    }

    private openEditDialog(station: CrudAtokuStation): void {
        const dialogRef = this.dialog.open(EditStationDialogComponent, {
            width: '500px',
            data: {
                station: {
                    id: station.id,
                    name: station.name,
                    category: station.category,
                    price: station.price,
                    subSpaceCode: station.subSpaceCode
                },
                categories: this.stationCategories
            } as EditStationDialogData
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!result) {
                return;
            }

            this.atokuService.updateStation(station.id, result).subscribe(() => this.loadStations());
        });
    }

    private removeStation(station: CrudAtokuStation): void {
        this.atokuService.deleteStation(station.id).subscribe(() => this.loadStations());
    }

    private refreshStations(): void {
        this.loadCategories();
        this.loadProfiles();
        this.loadStations();
    }

    private loadStations(): void {
        this.atokuService.getStations().subscribe(stations => {
            this.stations = stations;
            this.rebuildTableData();
        });
    }

    private loadCategories(): void {
        this.atokuService.getCategories().subscribe(categories => {
            this.stationCategories = categories
                .filter(category => String(category.code ?? '').toUpperCase() !== 'STOR')
                .map(category => category.displayName)
                .filter(Boolean);
        });
    }

    private loadProfiles(): void {
        this.atokuService.getPricingProfileLookup().subscribe(lookup => {
            this.pricingProfiles = lookup;
            if (!lookup.some(profile => profile.code === this.selectedPricingProfileCode)) {
                this.selectedPricingProfileCode = lookup[0]?.code ?? '';
            }
            this.onPricingProfileChange();
        });
    }

    private rebuildTableData(): void {
        this.tableData = this.stations
            .map(station => new CrudAtokuStation({
                id: String(station.id ?? ''),
                name: station.name ?? '',
                category: station.category ?? '',
                price: this.getSelectedProfilePrice(station),
                subSpaceCode: String(station.subSpaceCode ?? '').trim() || 'NaN'
            }));
    }

    private getSelectedProfilePrice(station: AtokuStation): number {
        const mapped = this.selectedProfilePrices[String(station.id)];
        if (mapped !== undefined) {
            return mapped;
        }
        return Number(station.price ?? 0);
    }

    private updatePriceColumnLabel(): void {
        const priceColumn = this.displayColumns.find(column => column.colName === 'price');
        if (priceColumn) {
            priceColumn.displayLabel = this.selectedPricingProfileCode
                ? `Price (${this.selectedPricingProfileCode})`
                : 'Price';
            priceColumn.exportLabel = priceColumn.displayLabel.toUpperCase();
            this.displayColumns = [...this.displayColumns];
        }
    }

    private getNextId(): number {
        const numericIds = this.stations
            .map(station => Number(station.id))
            .filter(id => Number.isFinite(id));
        return numericIds.length ? Math.max(...numericIds) + 1 : 1;
    }

    private toPriceMap(prices: unknown): Record<string, number> {
        if (!prices) {
            return {};
        }
        if (Array.isArray(prices)) {
            return prices.reduce<Record<string, number>>((acc, item) => {
                const row = item as Record<string, unknown>;
                const id = String(row['id'] ?? row['Id'] ?? '');
                if (!id) {
                    return acc;
                }
                acc[id] = Number(row['price'] ?? row['Price'] ?? 0);
                return acc;
            }, {});
        }
        if (typeof prices === 'object') {
            return Object.entries(prices as Record<string, unknown>).reduce<Record<string, number>>((acc, [key, value]) => {
                acc[String(key)] = Number(value ?? 0);
                return acc;
            }, {});
        }
        return {};
    }
}
